import React from "react"
import PropTypes from "prop-types"
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    MenuItem,
    Grid,
    InputAdornment
} from "@mui/material"

const ACCENTS = [
    { value: "#4f46e5", label: "Indigo" },
    { value: "#0d9488", label: "Teal" },
    { value: "#db2777", label: "Pink" },
    { value: "#c2410c", label: "Orange" },
    { value: "#2563eb", label: "Blue" },
    { value: "#9333ea", label: "Purple" },
    { value: "#16a34a", label: "Green" }
]

const emptyCourse = {
    title: "",
    description: "",
    subject_name: "",
    class_name: "",
    price: "",
    instructor: "",
    accent: "#4f46e5"
}

/** Create / edit form for a course. */
export default function CourseFormDialog({
    open,
    course,
    saving,
    onClose,
    onSubmit
}) {
    const [form, setForm] = React.useState(emptyCourse)
    const [errors, setErrors] = React.useState({})

    React.useEffect(() => {
        if (!open) return
        setErrors({})
        setForm(
            course
                ? {
                    title: course.title || "",
                    description: course.description || "",
                    subject_name: course.subject_name || "",
                    class_name: course.class_name || "",
                    price: course.price ?? "",
                    instructor: course.instructor || "",
                    accent: course.accent || "#4f46e5"
                }
                : emptyCourse
        )
    }, [open, course])

    const setField = (field) => (event) =>
        setForm((prev) => ({ ...prev, [field]: event.target.value }))

    const handleSubmit = () => {
        const nextErrors = {}
        if (!form.title.trim()) nextErrors.title = "Title is required"
        if (!form.subject_name.trim()) {
            nextErrors.subject_name = "Subject is required"
        }
        if (form.price === "" || Number(form.price) < 0) {
            nextErrors.price = "Enter a price (0 for a free course)"
        }

        setErrors(nextErrors)
        if (Object.keys(nextErrors).length) return

        onSubmit({
            ...form,
            class_name: form.class_name.trim(),
            price: Number(form.price)
        })
    }

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullWidth
            maxWidth="sm"
            /* Full screen below sm so the form is usable on a phone. */
            sx={{
                "& .MuiDialog-paper": {
                    m: { xs: 0, sm: 4 },
                    width: { xs: "100%", sm: "auto" },
                    maxHeight: { xs: "100%", sm: "calc(100% - 64px)" },
                    height: { xs: "100%", sm: "auto" },
                    borderRadius: { xs: 0, sm: 2 }
                }
            }}
        >
            <DialogTitle>{course ? "Edit course" : "Create a course"}</DialogTitle>

            <DialogContent dividers>
                <Grid container spacing={2} sx={{ pt: 1 }}>
                    <Grid size={{ xs: 12 }}>
                        <TextField
                            fullWidth
                            size="small"
                            label="Course title"
                            placeholder="Mathematics — Fractions Masterclass"
                            value={form.title}
                            onChange={setField("title")}
                            error={Boolean(errors.title)}
                            helperText={errors.title}
                        />
                    </Grid>

                    <Grid size={{ xs: 12, sm: 6 }}>
                        {/* Courses are platform-wide, so the class is a label, not a school's class. */}
                        <TextField
                            fullWidth
                            size="small"
                            label="Class (optional)"
                            placeholder="Class 6"
                            value={form.class_name}
                            onChange={setField("class_name")}
                        />
                    </Grid>

                    <Grid size={{ xs: 12, sm: 6 }}>
                        <TextField
                            fullWidth
                            size="small"
                            label="Subject"
                            placeholder="Mathematics"
                            value={form.subject_name}
                            onChange={setField("subject_name")}
                            error={Boolean(errors.subject_name)}
                            helperText={errors.subject_name}
                        />
                    </Grid>

                    <Grid size={{ xs: 12, sm: 6 }}>
                        <TextField
                            fullWidth
                            size="small"
                            type="number"
                            label="Price"
                            value={form.price}
                            onChange={setField("price")}
                            error={Boolean(errors.price)}
                            helperText={errors.price || "Enter 0 to make it free"}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        ₹
                                    </InputAdornment>
                                )
                            }}
                        />
                    </Grid>

                    <Grid size={{ xs: 12, sm: 6 }}>
                        <TextField
                            fullWidth
                            size="small"
                            label="Instructor"
                            value={form.instructor}
                            onChange={setField("instructor")}
                        />
                    </Grid>

                    <Grid size={{ xs: 12 }}>
                        <TextField
                            select
                            fullWidth
                            size="small"
                            label="Card colour"
                            value={form.accent}
                            onChange={setField("accent")}
                        >
                            {ACCENTS.map((item) => (
                                <MenuItem key={item.value} value={item.value}>
                                    <span className="flex items-center gap-2">
                                        <span
                                            className="inline-block h-3 w-3 rounded-full"
                                            style={{ background: item.value }}
                                        />
                                        {item.label}
                                    </span>
                                </MenuItem>
                            ))}
                        </TextField>
                    </Grid>

                    <Grid size={{ xs: 12 }}>
                        <TextField
                            fullWidth
                            multiline
                            minRows={3}
                            size="small"
                            label="Description"
                            value={form.description}
                            onChange={setField("description")}
                        />
                    </Grid>
                </Grid>
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 2 }}>
                <Button onClick={onClose} disabled={saving}>
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    onClick={handleSubmit}
                    disabled={saving}
                >
                    {course ? "Save changes" : "Create course"}
                </Button>
            </DialogActions>
        </Dialog>
    )
}

CourseFormDialog.propTypes = {
    open: PropTypes.bool.isRequired,
    course: PropTypes.object,
    saving: PropTypes.bool,
    onClose: PropTypes.func.isRequired,
    onSubmit: PropTypes.func.isRequired
}
